import axios from 'axios';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { IoIosShareAlt } from "react-icons/io";
import { PiArrowBendDoubleUpLeftFill, PiArrowBendUpLeftDuotone, PiBriefcaseDuotone, PiCheckCircleDuotone, PiCheckCircleFill, PiClockDuotone, PiCoinsDuotone, PiEnvelopeSimpleDuotone, PiFileTextDuotone, PiHeartDuotone, PiHeartFill, PiIdentificationCardDuotone, PiMapPinDuotone, PiPhoneDuotone, PiShareNetworkDuotone, PiShareNetworkFill, PiStudentDuotone, PiUserCircleDuotone } from "react-icons/pi";
import { PiSpinnerGapBold, PiUploadSimpleBold } from 'react-icons/pi';
import GenericAuthModal from 'components/GenericAuthModal';
import { CVForm } from 'components/Form';
import { Link } from 'react-router-dom';
import { JobListItem } from './JobListPage';
import Modal from 'components/Modal';
import FeedbackModal from 'components/FeedbackModal';
import AppStoreBadges from 'elements/AppStoreBadges';
import Pattern from 'elements/Pattern';

import { getColorConfig } from 'utils/StyleUtils'
import { getItemByKeyValue } from 'utils/GenericUtils';

import { useConfig } from 'providers/ConfigProvider';
import { useAuth } from 'providers/AuthProvider';

const FAVORITE_JOBS_KEY = 'favoriteJobs';
const FAVORITES_CHANGED_EVENT = 'favoriteJobsChanged';

const getFavoriteJobIds = () => {
    return JSON.parse(localStorage.getItem(FAVORITE_JOBS_KEY)) || [];
};

const setFavoriteJobIds = (ids) => {
    localStorage.setItem(FAVORITE_JOBS_KEY, JSON.stringify(ids));
    window.dispatchEvent(new Event(FAVORITES_CHANGED_EVENT));
};

export default function JobPage(props) {
    const [prettyUrl, setPrettyUrl] = useState(null);
    const [data, setData] = useState(null);
    const [similarJobs, setSimilarJobs] = useState([]);
    const [isSimilarJobsLoading, setIsSimilarJobsLoading] = useState(false);
    const [isGenericLoginModalOpen, setIsGenericLoginModalOpen] = useState(false);
    const [isFavorite, setIsFavorite] = useState(false);
    const [isApplying, setIsApplying] = useState(false);
    const [applyModal, setApplyModal] = useState({
        open: false,
        alreadyApplied: false,
        error: false,
    });
    const [cvUploadModal, setCvUploadModal] = useState({
        open: false,
        userData: null,
    });
    const handledApplyRequestRef = useRef(null);

    const { getCombo } = useConfig();
    const { refreshUserData } = useAuth();

    const getOfficeAddress = (officeId) => {
        const offices = getCombo('office') || [];
        const office = offices.find((item) => item.id === officeId);

        if (!office) return '';

        return `${office.zipcode} ${office.name}, ${office.address}`;
    };

    const navigate = useNavigate();
    let location = useLocation();

    useEffect(() => {
        if (location && location.pathname)
            setPrettyUrl(location.pathname.replace('/', ''));
    }, [location]);

    useEffect(() => {
        if (prettyUrl) {
            window.scrollTo(0, 0);
            const id = parseInt(prettyUrl.replace(/^\D+/g, ''));
            
            // Fetch main job advertisement
            const url = global.backingsEndpoint + 'jobAdvertisementControl/getJobAdvertisement';
            const body = { 'jobAdvertisementId': id };
            axios.post(url, body)
                .then(response => {
                    if (response.data) {
                        setData(response.data);
                        
                        // Fetch similar job advertisements
                        const similarJobsUrl = global.backingsEndpoint + 'jobAdvertisementControl/getSimilarJobAdvertisements';
                        const similarJobsBody = { 'jobAdvertisementId': id };
                        setSimilarJobs([]);
                        setIsSimilarJobsLoading(true);
                        axios.post(similarJobsUrl, similarJobsBody)
                            .then(similarResponse => {
                                setSimilarJobs(Array.isArray(similarResponse.data) ? similarResponse.data : []);
                            })
                            .catch(error => {
                                console.error('Error fetching similar jobs:', error);
                                setSimilarJobs([]);
                            })
                            .finally(() => setIsSimilarJobsLoading(false));
                    }
                })
                .catch(error => console.error('Error fetching job advertisement:', error));
        }
    }, [prettyUrl]);

    useEffect(() => {
        if (!data?.jobAdvertisementId) return;

        const syncFavorite = () => {
            setIsFavorite(getFavoriteJobIds().includes(data.jobAdvertisementId));
        };

        syncFavorite();

        window.addEventListener(FAVORITES_CHANGED_EVENT, syncFavorite);
        window.addEventListener('storage', syncFavorite);

        return () => {
            window.removeEventListener(FAVORITES_CHANGED_EVENT, syncFavorite);
            window.removeEventListener('storage', syncFavorite);
        };
    }, [data?.jobAdvertisementId]);
    

    const toggleGenericLoginModal = () => {
        setIsGenericLoginModalOpen(!isGenericLoginModalOpen); 
    };

    const toggleFavorite = () => {
        if (!data?.jobAdvertisementId) return;

        const savedFavorites = getFavoriteJobIds();

        const nextFavorites = savedFavorites.includes(data.jobAdvertisementId)
            ? savedFavorites.filter((id) => id !== data.jobAdvertisementId)
            : [...savedFavorites, data.jobAdvertisementId];

        setFavoriteJobIds(nextFavorites);
        setIsFavorite(nextFavorites.includes(data.jobAdvertisementId));
    };

    const registerForCurrentJob = useCallback(async () => {
        const registrationUrl = global.backingsEndpoint + 'employeeUserControl/jobRegistration';
        const response = await axios.post(registrationUrl, {
            jDto: {
                jobIdList: [data.jobAdvertisementId],
            },
        });

        if (response.data?.successful === false) {
            const registrationError = new Error('JOB_REGISTRATION_REJECTED');
            registrationError.validation = response.data;
            throw registrationError;
        }

        setApplyModal({
            open: true,
            alreadyApplied: false,
            error: false,
        });
        refreshUserData?.();
    }, [data?.jobAdvertisementId, refreshUserData]);

    const handleApply = useCallback(async () => {
        if (!data?.jobAdvertisementId) return;

        const token = localStorage.getItem('FD_TOKEN');

        if (!token) {
            setIsGenericLoginModalOpen(true);
            return;
        }

        try {
            setIsApplying(true);

            const loggedInClientUrl = global.backingsEndpoint + 'accessManagement/getLoggedInClientDto';

            const loggedInClientResponse = await axios.post(loggedInClientUrl);

            const loggedInUser = loggedInClientResponse.data || {};
            const registeredJobs = loggedInUser.jobAdvertisementList || [];

            const alreadyApplied = registeredJobs.map(Number).includes(Number(data.jobAdvertisementId));

            if (alreadyApplied) {
                setApplyModal({
                    open: true,
                    alreadyApplied: true,
                    error: false,
                });
                return;
            }

            const hasUploadedCv = loggedInUser.cvUploaded === true
                || (
                    (loggedInUser.cvUploaded === null || loggedInUser.cvUploaded === undefined)
                    && Boolean(loggedInUser.cvFileKey || loggedInUser.cvFile || loggedInUser.cvExpirationDate)
                );

            if (data.cvRequired === true && !hasUploadedCv) {
                setCvUploadModal({
                    open: true,
                    userData: loggedInUser,
                });
                return;
            }

            await registerForCurrentJob();
        } catch (error) {
            console.error('Job apply failed:', error);
            setApplyModal({
                open: true,
                alreadyApplied: false,
                error: true,
            });
        } finally {
            setIsApplying(false);
        }
    }, [data?.cvRequired, data?.jobAdvertisementId, registerForCurrentJob]);

    const handleCvUploadComplete = async () => {
        await registerForCurrentJob();
        setCvUploadModal({ open: false, userData: null });
    };

    useEffect(() => {
        const requestedJobId = location.state?.applyJobId;

        if (
            !requestedJobId ||
            Number(requestedJobId) !== Number(data?.jobAdvertisementId)
        ) {
            return;
        }

        const requestKey = `${location.key}:${requestedJobId}`;

        if (handledApplyRequestRef.current === requestKey) return;

        handledApplyRequestRef.current = requestKey;
        navigate(location.pathname, { replace: true, state: null });
        handleApply();
    }, [data?.jobAdvertisementId, handleApply, location.key, location.pathname, location.state, navigate]);

    if (data) {
        const color = getItemByKeyValue(getCombo('jobArea'), 'id', data.jobAreaId).color;

        return (
            <div className={'relative flex w-full flex-1 flex-col items-center overflow-hidden gradient-gray pb-site ' + (props.className || '')}>
                <Pattern className="pointer-events-none opacity-60" />

                <StickyActionBar
                    isFavorite={isFavorite}
                    onApply={handleApply}
                    onFavorite={toggleFavorite}
                    onShare={() => {
                        if (navigator.share) {
                            navigator.share({
                                title: data.positionName,
                                text: data.description?.replace(/<[^>]*>/g, ' ') || '',
                                url: window.location.href,
                            });
                        }
                    }}
                    onBack={() => navigate(-1)}
                />

                <div className="relative z-10 flex w-full flex-col gap-6 md:gap-8">
                <section className="flex w-full flex-col lg:h-[min(900px,calc(100svh-80px))] lg:min-h-[620px]">
                <div className='relative flex h-[clamp(420px,62svh,620px)] w-full flex-col items-center justify-end overflow-hidden rounded-b-3xl bg-cover bg-center bg-no-repeat p-6 shadow-2xl sm:p-8 md:p-16 lg:h-auto lg:min-h-0 lg:flex-1' style={{ backgroundImage: 'url(' + global.backingsEndpoint + 'photoRepository/getImageBySize?size=ORIGINAL&key=' + data.galleryImagePhotoKey + ')' }}>

                    <div className='absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/80' />

                    <div className='z-10 w-full'>
                        <JobHeader data={data} color={color} onApply={handleApply} />
                    </div>

                </div>
                    <div className='grid w-full grid-cols-1 gap-4 py-4 font-display sm:grid-cols-2 sm:py-4 md:py-4 xl:grid-cols-4'>
                        <InfoRow
                            icon={PiBriefcaseDuotone}
                            title="Kategória"
                            description={`${data.jobAreaName}, ${data.positionName}`}
                            gradientClassName="gradient-blue"
                        />

                        <InfoRow
                            icon={PiMapPinDuotone}
                            title="Munkavégzés helye"
                            description={`${data.countyName}, ${data.settlementName}`}
                            gradientClassName="gradient-green"
                        />

                        <InfoRow
                            icon={PiCoinsDuotone}
                            title="Fizetés"
                            description={`Bruttó ${data.grossSalaryPay}`}
                            gradientClassName="gradient-yellow"
                        />

                        <InfoRow
                            icon={PiStudentDuotone}
                            title="Betöltött kor"
                            description={data.under18 ? "18 éves kor alatt is" : "18 éves kor felett"}
                            gradientClassName="gradient-red"
                        />
                    </div>
                </section>

                <div className='mx-auto flex w-full flex-col items-center gap-6 px-site font-display md:gap-8'>
                    <div className='grid w-full gap-6 md:gap-8 lg:grid-cols-[minmax(0,3fr)_minmax(360px,2fr)]'>
                        <JobDetailSection icon={PiFileTextDuotone} title="Az állásról" content={data.description} accentClassName="text-green-600" />
                        <div className='grid w-full gap-6 md:gap-8'>
                            <JobDetailSection icon={PiClockDuotone} title="Munkaidő" content={data.workingTime} accentClassName="text-sky-600" />
                    {data.users?.length > 0 && (
                        <section className="w-full rounded-3xl bg-white p-4 text-neutral-800 shadow-2xl sm:p-5 md:p-6">
                            <div className="mb-4 flex items-center gap-3 font-display text-xl font-black text-neutral-900 sm:text-2xl">
                                <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-green-50 text-green-600">
                                    <PiUserCircleDuotone className="size-6" />
                                </span>
                                Kapcsolattartó
                            </div>

                            <div className="grid gap-3">
                                {data.users.map((user, index) => {
                                    const officeAddress = getOfficeAddress(data.officeId);

                                    return (
                                        <article key={user.email || index} className="flex flex-col gap-3 rounded-2xl bg-neutral-50 p-3 sm:flex-row sm:items-center sm:p-4">
                                            <img
                                                src={user.photoKey
                                                    ? `${global.backingsEndpoint}photoRepository/getImage?key=${user.photoKey}`
                                                    : '/assets/images/avatar.png'}
                                                alt={user.name}
                                                className="size-16 shrink-0 rounded-xl object-cover shadow-md sm:size-20"
                                            />

                                            <div className="min-w-0 flex-1">
                                                <h3 className="font-display text-lg font-black text-neutral-900 sm:text-xl">{user.name}</h3>
                                                <p className="mb-2 text-xs text-neutral-500 sm:text-sm">{user.post}</p>

                                                <div className="grid gap-1.5 text-xs text-neutral-700 sm:text-sm">
                                                    <Link to={`tel:+${user.countryCode}${user.serviceProviderNumber}${user.phoneNumber}`} className="flex min-w-0 items-center gap-2 transition hover:text-green-600">
                                                        <PiPhoneDuotone className="size-5 shrink-0 text-green-600" />
                                                        <span>+{user.countryCode} {user.serviceProviderNumber} {user.phoneNumber}</span>
                                                    </Link>
                                                    <Link to={`mailto:${user.email}`} className="flex min-w-0 items-center gap-2 transition hover:text-sky-600">
                                                        <PiEnvelopeSimpleDuotone className="size-5 shrink-0 text-sky-600" />
                                                        <span className="break-all">{user.email}</span>
                                                    </Link>
                                                    {officeAddress && (
                                                        <Link
                                                            to={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(officeAddress)}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="flex min-w-0 items-start gap-2 transition hover:text-orange-600"
                                                        >
                                                            <PiMapPinDuotone className="mt-0.5 size-5 shrink-0 text-orange-500" />
                                                            <span>{officeAddress}</span>
                                                        </Link>
                                                    )}
                                                </div>
                                            </div>
                                        </article>
                                    );
                                })}
                            </div>
                        </section>
                    )}
                    {data.cvRequired === true && (
                        <JobDetailSection
                            icon={PiIdentificationCardDuotone}
                            title="A jelentkezéshez önéletrajz feltöltése szükséges"
                            content="Amennyiben nem töltöttél még fel önéletrajzot, akkor a Jelentkezem az állásra gombra kattintást követő folyamat során feltöltheted."
                            accentClassName="text-sky-600"
                        />
                    )}
                        </div>
                    </div>
                    <section className="relative my-2 w-full max-w-2xl overflow-hidden rounded-3xl gradient-green p-6 text-center shadow-2xl sm:p-8">
                        <Pattern className="pointer-events-none opacity-30" />
                        <div className="relative z-10 flex flex-col items-center gap-4">
                            <p className="font-display text-2xl font-black text-white sm:text-3xl">Ez a munka neked való?</p>
                            <button
                                type='button'
                                className='flex w-full max-w-sm items-center justify-center rounded-xl bg-white px-6 py-4 text-center text-lg font-black text-green-700 shadow-xl ring-4 ring-white/20 transition hover:-translate-y-0.5 hover:shadow-2xl hover:ring-white/40 disabled:cursor-wait disabled:opacity-60 md:text-xl'
                                onClick={handleApply}
                                disabled={isApplying}
                            >
                                {isApplying ? 'Jelentkezés folyamatban…' : 'Jelentkezem az állásra'}
                            </button>
                        </div>
                    </section>

                    <GenericAuthModal
                        isOpen={isGenericLoginModalOpen}
                        onClose={toggleGenericLoginModal}
                        loginState={{
                            returnTo: location.pathname,
                            applyJobId: data.jobAdvertisementId,
                        }}
                    />
                    <CvRequiredUploadModal
                        open={cvUploadModal.open}
                        userData={cvUploadModal.userData}
                        onClose={() => setCvUploadModal({ open: false, userData: null })}
                        onComplete={handleCvUploadComplete}
                    />
                    <JobApplicationModal
                        isOpen={applyModal.open}
                        alreadyApplied={applyModal.alreadyApplied}
                        error={applyModal.error}
                        onClose={() =>
                            setApplyModal({
                                open: false,
                                alreadyApplied: false,
                                error: false,
                            })
                        }
                    />
                    <div className='grid w-full gap-6 lg:grid-cols-2'>
                        <InfoCard
                            imageSrc="/assets/images/details_share.jpg"
                            title="Dolgozzatok együtt!"
                            description={`
                                A haverokkal töltenéd el a munkaidőt, hogy a jó társaságban gyorsabban teljen?
                                Oszd meg munkaajánlatunkat velük, és dolgozzatok együtt!
                                Mi mindent meg fogunk tenni, hogy egy műszakba kerüljetek.
                            `}
                            actions={
                                <button
                                    type="button"
                                    className="gradient-blue flex items-center justify-center gap-2 rounded-xl px-6 py-3 font-display text-sm font-black text-white shadow-lg transition hover:-translate-y-0.5 hover:brightness-110 hover:shadow-xl md:text-base"
                                    onClick={() => {
                                        if (navigator.share) {
                                            navigator.share({
                                                title: 'Job Opportunity',
                                                text: 'Check out this amazing job opportunity!',
                                                url: window.location.href,
                                            }).catch((error) => console.error('Sharing failed:', error));
                                        } else {
                                            alert('Sharing is not supported on this device.');
                                        }
                                    }}
                                >
                                    <IoIosShareAlt className="size-6" />
                                    <div>Megosztom</div>
                                </button>
                            }
                        />

                        <InfoCard
                            imageSrc="/assets/images/details_app.jpg"
                            title="Töltsd le a Fürge APP-ot"
                            description={`
                                SOS és aktuális munkalehetőségek, érdekes és hasznos információk,
                                vagy fizu utalási értesítő… Ezekről mind első kézből értesülhetsz,
                                ha letöltöd applikációnkat.
                            `}
                            actions={
                                <AppStoreBadges compact className="justify-center" />
                            }
                        />
                    </div>
                </div>

                    <section className='relative w-full overflow-hidden rounded-3xl gradient-blue py-8 shadow-2xl sm:py-10 md:py-16'>
                        <Pattern className="pointer-events-none opacity-30" />
                        <div className="relative z-10 mx-auto flex w-full max-w-boxed flex-col gap-6 md:gap-16 px-site">
                        <div className='text-center font-display text-3xl font-black text-white md:text-5xl'>
                            Hasonló diákmunkák
                        </div>
                        <div className="flex flex-col gap-6 text-neutral-900">
                            {isSimilarJobsLoading && [...Array(3)].map((_, index) => <div key={index} className='h-[200px] w-full animate-pulse rounded-3xl bg-white/20' />)}
                            {!isSimilarJobsLoading && similarJobs.length > 0 && similarJobs.slice(0, 3).map((job) => <JobListItem key={job.jobAdvertisementId} data={job} />)}
                            {!isSimilarJobsLoading && similarJobs.length === 0 && (
                                <div className="w-full rounded-3xl bg-white/20 p-8 text-center font-display text-xl text-white">
                                    Jelenleg nincs hasonló diákmunka.
                                </div>
                            )}
                        </div>
                        </div>
                    </section>
                </div>
            </div>
        );
    } else {
        return (
            <div className="relative flex min-h-[520px] w-full flex-1 items-center justify-center overflow-hidden gradient-gray">
                <Pattern className="pointer-events-none opacity-60" />
                <div className="relative z-10 size-12 animate-spin rounded-full border-4 border-white/20 border-t-white" aria-label="Álláshirdetés betöltése" />
            </div>
        );
    }
}

function JobHeader (props) {
    const colorConfig = getColorConfig(props.color) || getColorConfig('blue');

    return (
        <div className='flex flex-col gap-5 md:gap-8 items-center justify-center text-center text-white'>
            <h1 className={`break-words font-display text-4xl font-black leading-[1.05] drop-shadow-lg md:text-6xl lg:text-7xl ${colorConfig.text}`}>
                {props.data.positionName.split('/').map((part, index) => (
                    <span key={index}>
                        {part}
                        {index < props.data.positionName.split('/').length - 1 && <wbr />}
                        {index < props.data.positionName.split('/').length - 1 && '/'}
                    </span>
                ))}
            </h1>
            <button
                type='button'
                className='button gradient-green'
                onClick={props.onApply}
            >
                Jelentkezem
            </button>
        </div>
    );
}

function InfoRow({ icon, title, description, gradientClassName }) {
    const Icon = icon;

    return (
        <div className={`flex min-w-0 items-center gap-3 rounded-3xl p-4 text-white shadow-2xl ${gradientClassName}`}>
            <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-white/20 shadow-inner">
                <Icon className="size-6" />
            </div>

            <div className="flex min-w-0 flex-col gap-1">
                <div className="font-display text-base font-black leading-tight xl:text-lg">
                    {title}
                </div>

                <div className='text-sm leading-snug text-white/85'>
                    {description}
                </div>
            </div>
        </div>
    );
}

function JobDetailSection({ icon: Icon, title, content, accentClassName = 'text-green-600' }) {
    return (
        <section className='relative w-full rounded-3xl bg-white p-6 text-neutral-800 shadow-2xl sm:p-8 md:p-10'>
            <h2 className="mb-6 flex items-center gap-3 font-display text-2xl font-black leading-tight text-neutral-900 sm:text-3xl">
                <span className={`flex size-11 shrink-0 items-center justify-center rounded-xl bg-neutral-100 sm:size-12 ${accentClassName}`}>
                    <Icon className='size-7 sm:size-8' />
                </span>
                <span>{title}</span>
            </h2>
            <div
                className='text-sm leading-relaxed text-neutral-700 sm:text-base [&_li]:pl-1 [&_p:not(:last-child)]:mb-4 [&_strong]:font-black [&_ul]:my-4 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-6'
                dangerouslySetInnerHTML={{ __html: content }}
            />
        </section>
    );
}

function InfoCard({ imageSrc, title, description, actions }) {
    return (
        <article className="flex h-full flex-col overflow-hidden rounded-3xl bg-white text-center text-neutral-800 shadow-2xl xl:flex-row xl:text-left">
            <img
                src={imageSrc}
                alt={title}
                className="aspect-[16/8] w-full object-cover xl:aspect-auto xl:w-[42%]"
            />
            <div className="flex min-w-0 flex-1 flex-col p-5 sm:p-6">
                <h3 className="font-display text-2xl font-black leading-tight text-neutral-900">
                    {title}
                </h3>
                <p className="flex-1 py-4 text-sm leading-relaxed text-neutral-600 sm:text-base">
                    {description}
                </p>
                <div className="flex justify-center gap-4 xl:justify-start">
                    {actions}
                </div>
            </div>
        </article>
    );
}

function StickyActionBar({ isFavorite, onApply, onFavorite, onShare, onBack }) {
    return (
        <div className='fixed bottom-3 left-1/2 z-40 flex -translate-x-1/2 flex-row gap-3 rounded-2xl border border-white/60 bg-white/85 p-2.5 shadow-2xl backdrop-blur-xl sm:bottom-auto sm:left-auto sm:right-4 sm:top-1/2 sm:-translate-y-1/2 sm:translate-x-0 sm:flex-col sm:p-3'>
            <button
                type='button'
                onClick={onFavorite}
                className='group text-red-600 hover:scale-110 transition'
            >
                {isFavorite ? (
                    <PiHeartFill className='size-10' />
                ) : (
                    <>
                        <PiHeartDuotone className='size-10 group-hover:hidden' />
                        <PiHeartFill className='size-10 hidden group-hover:block' />
                    </>
                )}
            </button>

            <button
                type='button'
                onClick={onApply}
                className='group text-lime-500 hover:scale-110 transition'
            >
                <>
                    <PiCheckCircleDuotone className='size-10 group-hover:hidden' />
                    <PiCheckCircleFill className='size-10 hidden group-hover:block' />
                </>
            </button>

            <button
                type='button'
                onClick={onShare}
                className='group text-sky-400 hover:scale-110 transition'
            >
                <>
                    <PiShareNetworkDuotone className='size-10 group-hover:hidden' />
                    <PiShareNetworkFill className='size-10 hidden group-hover:block' />
                </>
            </button>

            <button
                type='button'
                onClick={onBack}
                className='group text-orange-400 hover:scale-110 transition'
            >
                <>
                    <PiArrowBendUpLeftDuotone className='size-10 group-hover:hidden' />
                    <PiArrowBendDoubleUpLeftFill className='size-10 hidden group-hover:block' />
                </>
            </button>
        </div>
    );
}

function CvRequiredUploadModal({ open, userData, onClose, onComplete }) {
    const cvValueRef = useRef({});
    const [formErrors, setFormErrors] = useState(null);
    const [saveError, setSaveError] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;

        cvValueRef.current = {
            cvFile: userData?.cvFileKey ?? userData?.cvFile ?? null,
            cvAcceptTerms: userData?.cvAcceptTerms ?? false,
            cvUploaded: userData?.cvUploaded ?? false,
        };
        setFormErrors(null);
        setSaveError(null);
        setSaving(false);
    }, [open, userData]);

    const saveCvAndApply = async () => {
        if (saving) return;

        const dto = cvValueRef.current || {};
        const cvFile = dto.cvFile ?? dto.cvFileKey ?? null;
        const nextErrors = {};

        if (!cvFile) {
            nextErrors.cvFileKey = [{ message: 'A jelentkezéshez töltsd fel az önéletrajzodat.' }];
        }
        if (!dto.cvAcceptTerms) {
            nextErrors.cvAcceptTerms = [{ message: 'A folytatáshoz el kell fogadnod az adatkezelési feltételeket.' }];
        }

        if (Object.keys(nextErrors).length) {
            setFormErrors(nextErrors);
            setSaveError('Töltsd fel az önéletrajzodat, és fogadd el a feltételeket a jelentkezés folytatásához.');
            return;
        }

        setSaving(true);
        setFormErrors(null);
        setSaveError(null);

        try {
            const response = await axios.post(
                global.backingsEndpoint + 'employeeUserControl/profileCvModification',
                {
                    dto: {
                        cvFile,
                        cvAcceptTerms: true,
                        cvUploaded: true,
                    },
                }
            );

            if (!response.data?.successful) {
                const properties = response.data?.properties || {};
                setFormErrors({
                    ...properties,
                    cvFileKey: properties.cvFileKey || properties.cvFile,
                });
                setSaveError(
                    response.data?.errorMessages?.[0]
                    || 'Az önéletrajz mentése nem sikerült. Kérjük, ellenőrizd a megadott adatokat.'
                );
                return;
            }

            await onComplete();
        } catch (error) {
            console.error('CV upload and job application failed:', error);
            setSaveError('A mentés vagy a jelentkezés nem sikerült. Kérjük, próbáld újra később.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose} maxWidth="max-w-[680px]" className="!w-full" showClose={!saving}>
            <div className="flex flex-col">
                <div className="flex items-center gap-3 px-5 py-4 text-white sm:px-6">
                    <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-white/15 text-lime-300">
                        <PiUploadSimpleBold className="size-6" />
                    </span>
                    <div>
                        <h2 className="font-display text-2xl font-black leading-tight sm:text-3xl">Önéletrajz szükséges</h2>
                        <p className="mt-1 text-xs font-semibold text-white/65 sm:text-sm">Töltsd fel, és innen folytatjuk a jelentkezést.</p>
                    </div>
                </div>

                <div className="mx-2 rounded-2xl bg-white p-4 text-neutral-900 shadow-2xl sm:p-5">
                    <CVForm
                        edit
                        variant="registration"
                        color="text-green-700"
                        backgroundColor="gradient-green"
                        borderColor="border-green-500"
                        className="!border-0 !p-0 !shadow-none"
                        value={userData}
                        error={formErrors}
                        onValueChange={({ value }) => {
                            cvValueRef.current = value;
                            setFormErrors(null);
                            setSaveError(null);
                        }}
                    />

                    {saveError && (
                        <p className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{saveError}</p>
                    )}
                </div>

                <div className="grid gap-3 p-4 sm:grid-cols-2">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={saving}
                        className="order-2 rounded-xl bg-white/15 px-5 py-3.5 font-black text-white transition hover:bg-white/25 disabled:opacity-50 sm:order-1"
                    >
                        Mégsem
                    </button>
                    <button
                        type="button"
                        onClick={saveCvAndApply}
                        disabled={saving}
                        className="gradient-green order-1 flex items-center justify-center gap-2 rounded-xl px-5 py-3.5 font-black text-white shadow-lg transition hover:brightness-110 disabled:cursor-wait disabled:opacity-60 sm:order-2"
                    >
                        {saving ? <><PiSpinnerGapBold className="animate-spin" /> Mentés…</> : 'Jelentkezem az állásra'}
                    </button>
                </div>
            </div>
        </Modal>
    );
}

function JobApplicationModal({ isOpen, alreadyApplied, error, onClose }) {
    const tone = error ? 'error' : alreadyApplied ? 'warning' : 'success';
    const title = error
        ? 'A jelentkezés nem sikerült'
        : alreadyApplied
            ? 'Már jelentkeztél erre a munkára!'
            : 'Köszönjük jelentkezésed!';
    const message = error
        ? 'A jelentkezésedet most nem sikerült elküldeni. Kérjük, próbáld újra később.'
        : alreadyApplied
            ? 'Ehhez az álláshoz már korábban elküldted a jelentkezésedet.'
            : 'Kollégáink hamarosan keresni fognak telefonon!';

    return (
        <FeedbackModal
            open={isOpen}
            onClose={onClose}
            tone={tone}
            title={title}
            message={message}
            actionLabel="Rendben"
        >
            {!alreadyApplied && !error && (
                <div className="mt-6 border-t border-neutral-200 pt-5">
                    <p className="mb-2 text-lg font-black">
                        Letöltötted már a Fürge Diák Applikációját?
                    </p>

                    <p className="mb-5 text-sm text-neutral-600">
                        Töltsd le a Fürge Diák alkalmazását, és értesülj elsőként a munkalehetőségekről.
                    </p>

                    <AppStoreBadges compact className="justify-center sm:justify-start" />
                </div>
            )}
        </FeedbackModal>
    );
}
